import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, LogOut, Star, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title: "",
  slug: "",
  tag: "Essay",
  date: "",
  read_time: "4 min read",
  image: "",
  excerpt: "",
  content: "",
  title_de: "",
  tag_de: "",
  read_time_de: "",
  excerpt_de: "",
  content_de: "",
};

const inputClass =
  "w-full bg-transparent border border-line focus:border-cobalt outline-none px-4 py-3 text-sm placeholder:text-cocoa/50 transition-colors duration-300";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.25em] uppercase font-semibold text-cocoa mb-2">{label}</label>
    {children}
  </div>
);

const UploadButton = ({ onUploaded, uploading, testid }) => (
  <label
    data-testid={testid}
    className={`shrink-0 inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-cobalt text-cobalt px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-cobalt hover:text-cream transition-colors duration-300 ${uploading ? "opacity-60 pointer-events-none" : ""}`}
  >
    <ImagePlus size={13} />
    {uploading ? "Uploading…" : "Upload"}
    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onUploaded} disabled={uploading} />
  </label>
);

export default function Editor() {
  const [auth, setAuth] = useState(null);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [notice, setNotice] = useState("");
  const [entries, setEntries] = useState([]);
  const [bForm, setBForm] = useState(null);
  const [bEditingId, setBEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/upload`, { method: "POST", credentials: "include", body: fd });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || "Upload failed");
    }
    return (await res.json()).url;
  };

  const handleUpload = (apply) => async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file);
      apply(url);
      setNotice("Photo uploaded.");
    } catch (err) {
      setNotice(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const BEYOND_CATS = ["reading", "photography", "traveling", "journaling", "coffee-culture", "growth"];
  const emptyBeyond = { category: "traveling", title: "", text: "", image: "", location: "", date: "" };

  const load = () =>
    fetch(`${API}/journal`)
      .then((r) => r.json())
      .then(setPosts)
      .catch(() => {});

  const loadBeyond = () =>
    fetch(`${API}/beyond`)
      .then((r) => r.json())
      .then(setEntries)
      .catch(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => {
        setAuth(r.ok);
        if (r.ok) {
          load();
          loadBeyond();
        }
      })
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
    if (res.ok) {
      setAuth(true);
      load();
      loadBeyond();
    } else {
      setLoginError("Invalid password — please try again.");
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setAuth(false);
  };

  const startEdit = (p) => {
    setEditingSlug(p.slug);
    setNotice("");
    setForm({
      ...emptyForm,
      ...p,
      content: (p.content || []).join("\n\n"),
      content_de: (p.content_de || []).join("\n\n"),
    });
    window.scrollTo(0, 0);
  };

  const startNew = () => {
    setEditingSlug(null);
    setNotice("");
    setForm({ ...emptyForm });
    window.scrollTo(0, 0);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      content: form.content.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
      content_de: form.content_de
        ? form.content_de.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean)
        : null,
    };
    ["slug", "title_de", "tag_de", "read_time_de", "excerpt_de"].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });
    const res = await fetch(editingSlug ? `${API}/journal/${editingSlug}` : `${API}/journal`, {
      method: editingSlug ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      setNotice(editingSlug ? "Essay updated." : "Essay published.");
      setForm(null);
      setEditingSlug(null);
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setNotice(typeof d.detail === "string" ? d.detail : "Save failed — please check the fields.");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this essay? This cannot be undone.")) return;
    await fetch(`${API}/journal/${slug}`, { method: "DELETE", credentials: "include" });
    load();
  };

  const feature = async (slug) => {
    await fetch(`${API}/journal/${slug}/feature`, { method: "POST", credentials: "include" });
    load();
    setNotice("Featured essay updated.");
  };

  const saveBeyond = async (e) => {
    e.preventDefault();
    const res = await fetch(bEditingId ? `${API}/beyond/${bEditingId}` : `${API}/beyond`, {
      method: bEditingId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(bForm),
    });
    if (res.ok) {
      setNotice(bEditingId ? "Entry updated." : "Entry added to Beyond Academics.");
      setBForm(null);
      setBEditingId(null);
      loadBeyond();
    } else {
      setNotice("Save failed — please check the fields.");
    }
  };

  const removeBeyond = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await fetch(`${API}/beyond/${id}`, { method: "DELETE", credentials: "include" });
    loadBeyond();
  };

  const startEditBeyond = (entry) => {
    setBEditingId(entry.id);
    setBForm({
      category: entry.category,
      title: entry.title,
      text: entry.text || "",
      image: entry.image || "",
      location: entry.location || "",
      date: entry.date || "",
    });
    window.scrollTo(0, document.body.scrollHeight);
  };

  if (auth === null) {
    return <main className="min-h-screen bg-cream" />;
  }

  if (!auth) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="editor-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-line p-10" data-testid="editor-login-form">
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-cobalt">Private</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight">Journal Editor</h1>
          <p className="mt-3 text-sm text-cocoa leading-relaxed">
            This page is just for you, Sahithi. Enter your passphrase to write and publish essays.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passphrase"
            data-testid="editor-password-input"
            className={`${inputClass} mt-6`}
            required
          />
          {loginError && (
            <p className="mt-3 text-sm text-red-700" data-testid="editor-login-error">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            data-testid="editor-login-button"
            className="mt-6 w-full rounded-full bg-choco text-cream py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-cobalt transition-colors duration-400"
          >
            Unlock editor
          </button>
          <Link to="/" className="mt-6 flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-cocoa hover:text-choco transition-colors duration-300">
            <ArrowLeft size={13} /> Back to portfolio
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream" data-testid="editor-page">
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-24">
        <div className="flex items-center justify-between">
          <Link to="/journal" className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-semibold text-cocoa hover:text-choco transition-colors duration-300" data-testid="editor-back-link">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            View journal
          </Link>
          <button onClick={logout} data-testid="editor-logout-button" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-cocoa hover:text-cobalt transition-colors duration-300">
            <LogOut size={13} /> Log out
          </button>
        </div>

        <div className="mt-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-cobalt">Journal Editor</p>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">Your essays</h1>
          </div>
          <button onClick={startNew} data-testid="editor-new-button" className="group inline-flex items-center gap-2 rounded-full bg-choco text-cream px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold hover:bg-cobalt transition-colors duration-400">
            <Plus size={14} /> New essay
          </button>
        </div>

        {notice && (
          <p className="mt-6 border border-line bg-ivory/50 px-5 py-3 text-sm" data-testid="editor-notice">
            {notice}
          </p>
        )}

        {form && (
          <form onSubmit={save} className="mt-10 border border-line p-8 space-y-6 bg-ivory/30" data-testid="editor-form">
            <p className="font-serif italic text-2xl tracking-tight">
              {editingSlug ? `Editing — ${editingSlug}` : "New essay"}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Title (English) *">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} data-testid="editor-title-input" />
              </Field>
              <Field label="Slug (optional — auto from title)">
                <input value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputClass} placeholder="my-new-essay" disabled={!!editingSlug} />
              </Field>
              <Field label="Tag">
                <input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Date">
                <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputClass} placeholder="July 2026" />
              </Field>
              <Field label="Read time">
                <input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Image">
                <div className="flex items-center gap-3">
                  <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputClass} placeholder="https://… or upload →" />
                  <UploadButton uploading={uploading} testid="editor-upload-journal" onUploaded={handleUpload((url) => setForm((f) => ({ ...f, image: url })))} />
                </div>
                {form.image && (
                  <img src={form.image} alt="preview" className="mt-3 h-20 w-20 rounded-lg object-cover border border-line" data-testid="editor-image-preview" />
                )}
              </Field>
            </div>
            <Field label="Excerpt (English)">
              <textarea rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`${inputClass} resize-none`} />
            </Field>
            <Field label="Essay text (English) — separate paragraphs with a blank line">
              <textarea rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputClass} resize-y`} data-testid="editor-content-input" />
            </Field>

            <div className="pt-6 border-t border-line">
              <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-cobalt">German version (optional)</p>
              <div className="mt-5 grid md:grid-cols-2 gap-6">
                <Field label="Titel (Deutsch)">
                  <input value={form.title_de || ""} onChange={(e) => setForm({ ...form, title_de: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Tag (Deutsch)">
                  <input value={form.tag_de || ""} onChange={(e) => setForm({ ...form, tag_de: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Lesezeit (Deutsch)">
                  <input value={form.read_time_de || ""} onChange={(e) => setForm({ ...form, read_time_de: e.target.value })} className={inputClass} placeholder="4 Min. Lesezeit" />
                </Field>
              </div>
              <div className="mt-6 space-y-6">
                <Field label="Auszug (Deutsch)">
                  <textarea rows={3} value={form.excerpt_de || ""} onChange={(e) => setForm({ ...form, excerpt_de: e.target.value })} className={`${inputClass} resize-none`} />
                </Field>
                <Field label="Essay-Text (Deutsch) — Absätze mit Leerzeile trennen">
                  <textarea rows={10} value={form.content_de || ""} onChange={(e) => setForm({ ...form, content_de: e.target.value })} className={`${inputClass} resize-y`} />
                </Field>
              </div>
            </div>

            <div className="flex gap-4 pt-2">
              <button type="submit" data-testid="editor-save-button" className="rounded-full bg-choco text-cream px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-cobalt transition-colors duration-400">
                {editingSlug ? "Save changes" : "Publish essay"}
              </button>
              <button type="button" onClick={() => { setForm(null); setEditingSlug(null); }} data-testid="editor-cancel-button" className="rounded-full border border-choco px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-choco hover:text-cream transition-colors duration-400">
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="mt-10 border-t border-line" data-testid="editor-list">
          {posts.map((p) => (
            <div key={p.slug} className="flex items-center gap-6 py-6 border-b border-line group">
              <div className="flex-1 min-w-0">
                <p className="font-serif text-xl md:text-2xl tracking-tight truncate">{p.title}</p>
                <p className="mt-1 text-xs tracking-[0.18em] uppercase text-cocoa">
                  {p.slug} · {p.date} {p.title_de ? <span className="text-cobalt">· DE ✓</span> : ""}
                </p>
              </div>
              <button
                onClick={() => feature(p.slug)}
                data-testid={`editor-feature-${p.slug}`}
                title="Set as featured essay"
                className={`inline-flex items-center justify-center h-9 w-9 rounded-full border transition-colors duration-300 ${
                  p.featured ? "bg-cobalt border-cobalt text-cream" : "border-line text-cocoa hover:border-cobalt hover:text-cobalt"
                }`}
              >
                <Star size={15} fill={p.featured ? "currentColor" : "none"} />
              </button>
              <button onClick={() => startEdit(p)} data-testid={`editor-edit-${p.slug}`} className="inline-flex items-center gap-2 rounded-full border border-choco px-5 py-2 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-choco hover:text-cream transition-colors duration-300">
                <Pencil size={13} /> Edit
              </button>
              <button onClick={() => remove(p.slug)} data-testid={`editor-delete-${p.slug}`} className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2 text-xs tracking-[0.15em] uppercase font-semibold text-cocoa hover:border-red-700 hover:text-red-700 transition-colors duration-300">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-20 pt-14 border-t border-line" data-testid="editor-beyond-section">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-cobalt">Beyond Academics</p>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">Interest pages</h2>
              <p className="mt-2 text-sm text-cocoa max-w-md">
                Post photos and notes to your interest pages — they appear at /beyond/traveling, /beyond/coffee-culture and so on.
              </p>
            </div>
            <button
              onClick={() => { setBForm({ ...emptyBeyond }); setBEditingId(null); window.scrollTo(0, document.body.scrollHeight); }}
              data-testid="editor-beyond-new-button"
              className="group inline-flex items-center gap-2 rounded-full bg-choco text-cream px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold hover:bg-cobalt transition-colors duration-400"
            >
              <Plus size={14} /> New entry
            </button>
          </div>

          {bForm && (
            <form onSubmit={saveBeyond} className="mt-8 border border-line p-8 space-y-6 bg-ivory/40" data-testid="editor-beyond-form">
              <p className="font-serif italic text-2xl tracking-tight">
                {bEditingId ? "Edit entry" : "New entry"}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Page / category">
                  <select
                    value={bForm.category}
                    onChange={(e) => setBForm({ ...bForm, category: e.target.value })}
                    className={inputClass}
                    data-testid="editor-beyond-category"
                  >
                    {BEYOND_CATS.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Title *">
                  <input required value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} className={inputClass} data-testid="editor-beyond-title" />
                </Field>
                <Field label="Image">
                  <div className="flex items-center gap-3">
                    <input value={bForm.image} onChange={(e) => setBForm({ ...bForm, image: e.target.value })} className={inputClass} placeholder="/photos/… or upload →" />
                    <UploadButton uploading={uploading} testid="editor-upload-beyond" onUploaded={handleUpload((url) => setBForm((f) => ({ ...f, image: url })))} />
                  </div>
                  {bForm.image && (
                    <img src={bForm.image} alt="preview" className="mt-3 h-20 w-20 rounded-lg object-cover border border-line" />
                  )}
                </Field>
                <Field label="Location">
                  <input value={bForm.location} onChange={(e) => setBForm({ ...bForm, location: e.target.value })} className={inputClass} placeholder="New York, USA" />
                </Field>
                <Field label="Date">
                  <input value={bForm.date} onChange={(e) => setBForm({ ...bForm, date: e.target.value })} className={inputClass} placeholder="December 2025" />
                </Field>
              </div>
              <Field label="Note / caption">
                <textarea rows={3} value={bForm.text} onChange={(e) => setBForm({ ...bForm, text: e.target.value })} className={`${inputClass} resize-none`} />
              </Field>
              <div className="flex gap-4">
                <button type="submit" data-testid="editor-beyond-save" className="rounded-full bg-choco text-cream px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-cobalt transition-colors duration-400">
                  {bEditingId ? "Save changes" : "Add entry"}
                </button>
                <button type="button" onClick={() => { setBForm(null); setBEditingId(null); }} className="rounded-full border border-choco px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-choco hover:text-cream transition-colors duration-400">
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 border-t border-line">
            {entries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-5 py-5 border-b border-line">
                {entry.image && (
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-ivory">
                    <img src={entry.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg tracking-tight truncate">{entry.title}</p>
                  <p className="mt-0.5 text-xs tracking-[0.18em] uppercase text-cocoa">
                    <span className="text-cobalt font-semibold">{entry.category}</span> · {entry.date} {entry.location ? `· ${entry.location}` : ""}
                  </p>
                </div>
                <button onClick={() => startEditBeyond(entry)} data-testid={`editor-beyond-edit-${entry.id}`} className="inline-flex items-center gap-2 rounded-full border border-choco px-4 py-2 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-choco hover:text-cream transition-colors duration-300">
                  <Pencil size={13} />
                </button>
                <button onClick={() => removeBeyond(entry.id)} data-testid={`editor-beyond-delete-${entry.id}`} className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-xs tracking-[0.15em] uppercase font-semibold text-cocoa hover:border-red-700 hover:text-red-700 transition-colors duration-300">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
